/**
 * AuditPage.tsx — Audit & Discrepanze surface orchestrator. Resolves which
 * project to show (route param, else most-recent), fetches its live audit data
 * (categories / line items / uncategorized) from the tender-api, and composes
 * Header + ActionPanel + UnifiedTable. Categorization is a real mutation; the
 * discrepancy status/comments are handled inside DiscrepancyContent.
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { apiErrorMessage } from "../../lib/api";
import {
  DOC_ROLE_LABEL,
  missingRolesFromError,
  useAuditCategories,
  useAuditLineItems,
  useBulkCategorizeLineItems,
  useCategorizeLineItem,
  useLatestAnalysisJob,
  useProject,
  useProjects,
  useStartAnalysis,
} from "../../lib/tenderApi";
import { ActionPanel } from "./ActionPanel";
import { Header } from "./Header";
import { UnifiedTable } from "./UnifiedTable";

function SectionHeader({ title, sub }: { title: string; sub: string }): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        marginBottom: 10,
      }}
    >
      <div>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0, letterSpacing: "-0.005em" }}>
          {title}
        </h2>
        <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
}

export function AuditPage(): JSX.Element {
  // Resolve which project's audit to show: the route param if present (deep
  // link from Ingest), otherwise the most-recently-updated project.
  const { projectId } = useParams<{ projectId?: string }>();
  const projectsQuery = useProjects();
  const explicitQuery = useProject(projectId ?? null);
  const project = projectId ? explicitQuery.data : projectsQuery.data?.[0];
  const projectLoading = projectId ? explicitQuery.isLoading : projectsQuery.isLoading;
  const projectError = projectId
    ? explicitQuery.isError
      ? apiErrorMessage(explicitQuery.error)
      : null
    : projectsQuery.isError
      ? apiErrorMessage(projectsQuery.error)
      : null;
  const pid = project?.id ?? null;

  // The analysis lifecycle for this project: rediscovers an in-flight run
  // after reload/reconnect (it polls while active) and drives the table's
  // empty-state (progress / failed / never-analyzed).
  const latestJob = useLatestAnalysisJob(pid).data ?? null;
  const startAnalysis = useStartAnalysis();
  const startMissing = missingRolesFromError(startAnalysis.error);
  const startError = startAnalysis.isError
    ? startMissing
      ? `Analisi non avviata — manca: ${startMissing.map((r) => DOC_ROLE_LABEL[r]).join(", ")}.`
      : apiErrorMessage(startAnalysis.error)
    : null;

  // Live audit data for the resolved project.
  const categoriesQuery = useAuditCategories(pid);
  const lineItemsQuery = useAuditLineItems(pid);
  const uncatQuery = useAuditLineItems(pid, { uncategorized: true });
  const categorize = useCategorizeLineItem(pid ?? "");
  const bulkCategorize = useBulkCategorizeLineItems(pid ?? "");

  const categories = categoriesQuery.data ?? [];
  const lineItems = lineItemsQuery.data ?? [];
  const uncat = uncatQuery.data ?? [];
  const auditLoading = pid != null && (lineItemsQuery.isLoading || categoriesQuery.isLoading);
  const auditError = lineItemsQuery.isError
    ? apiErrorMessage(lineItemsQuery.error)
    : categoriesQuery.isError
      ? apiErrorMessage(categoriesQuery.error)
      : null;
  const busy = categorize.isPending || bulkCategorize.isPending;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());

  function toggleSel(id: string): void {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function selectAll(checked: boolean): void {
    setSelected(checked ? new Set(uncat.map((t) => t.id)) : new Set());
  }

  function onAssign(itemId: string, categoryId: string): void {
    categorize.mutate({ lineItemId: itemId, categoryId });
    setSelected((s) => {
      const n = new Set(s);
      n.delete(itemId);
      return n;
    });
  }

  function onBulkAssign(categoryId: string): void {
    if (selected.size === 0) return;
    bulkCategorize.mutate(
      { ids: [...selected], categoryId },
      { onSuccess: () => setSelected(new Set()) },
    );
  }

  function toggleCat(id: string): void {
    setExpandedCats((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  if (projectError) {
    return (
      <div style={{ padding: "20px 28px 60px" }}>
        <div className="card" style={{ padding: 24, textAlign: "center" }}>
          <div style={{ color: "var(--c-err)", fontSize: 13, marginBottom: 10 }}>
            {projectError}
          </div>
          <Link to="/" className="btn btn-sm">
            Torna ai progetti
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 28px 60px" }}>
      <Header key={pid ?? "none"} project={project} loading={projectLoading} />

      {/* Hide the whole "Azioni richieste" section once everything is
          categorized — an empty panel is just dead space. */}
      {uncat.length > 0 && (
        <>
          <SectionHeader title="Azioni richieste" sub={`${uncat.length} voci da categorizzare`} />
          <ActionPanel
            items={uncat}
            categories={categories}
            selected={selected}
            toggleSel={toggleSel}
            selectAll={selectAll}
            onAssign={onAssign}
            onBulkAssign={onBulkAssign}
            busy={busy}
          />
          <div style={{ height: 28 }} />
        </>
      )}

      <SectionHeader title="Categorie e voci" sub="Drill-down per categoria" />
      <UnifiedTable
        categories={categories}
        lineItems={lineItems}
        loading={auditLoading}
        error={auditError}
        projectId={pid ?? ""}
        openRow={openRow}
        openDiscrepancy={(item) => setOpenRow((cur) => (cur === item.id ? null : item.id))}
        expandedCats={expandedCats}
        toggleCat={toggleCat}
        analysisJob={latestJob}
        onStartAnalysis={pid ? () => startAnalysis.mutate(pid) : undefined}
        startPending={startAnalysis.isPending}
        startError={startError}
      />
    </div>
  );
}
